import * as React from 'react';
import { FieldValues } from 'react-hook-form';
import { Box } from '@mui/system';
import useMultiStep, { FormStep } from './useMultiStep';
import MultiStepFormHeader from './MultiStepFormHeader';
import { MultiStepContextProps } from './types';
import MultiStepFormFooter from './MultiStepFormFooter';
import { ModelForm } from '../../../forms/types';

/**
 * Onglet additionnel (lecture seule) affiché à droite de la barre de navigation,
 * après les étapes du formulaire. Son panneau est rendu sans le footer du
 * formulaire (pas de précédent/suivant/enregistrer).
 */
export type MultiStepExtraTab = {
    tabName: string;
    title: React.ReactNode;
    panel: React.ReactNode;
};

export type MultiStepFormProps = {
    steps: FormStep<MultiStepContextProps>[];
    initiaValues: FieldValues;
    submitAction: (fieldValues: FieldValues) => void;
    isCreate?: boolean;
    /** Action d'annulation (navigation) ; à défaut le footer ferme la modale. */
    onCancel?: () => void;
    /** Notifie la fiche parente du libellé courant pour l'afficher dans l'en-tête en création. */
    onLabelChange?: (value: string) => void;
    /** Onglets additionnels en lecture seule, poussés à droite de la barre. */
    extraTabs?: MultiStepExtraTab[];
} & Pick<ModelForm<unknown>, 'afterSubmit' | 'isCreate'>;

const MultiStepForm: React.FunctionComponent<MultiStepFormProps> = function ({
    steps,
    initiaValues,
    submitAction,
    isCreate = true,
    onCancel,
    onLabelChange,
    extraTabs = [],
}) {
    const multiStepForm = useMultiStep(steps, initiaValues, submitAction);
    const {
        currentStep: { step },
    } = multiStepForm;

    // Onglet additionnel actif (null = on est sur une étape de formulaire).
    const [activeExtraIndex, setActiveExtraIndex] = React.useState<number | null>(null);

    const { component: CurrentStep } = step;
    const multiStepProps = {
        steps,
        multiStepForm,
        submitAction,
        isCreate,
        onCancel,
        onLabelChange,
    };

    // Validations transverses (évaluées sur l'ensemble des valeurs accumulées) :
    // un message par onglet en erreur, et un blocage global de l'enregistrement.
    // L'onglet courant est ignoré ici : sa validité est déjà gérée par `isStepValid`
    // (son `formContext` est monté), et ses valeurs ne sont pas encore persistées
    // dans `formValues` tant qu'on ne l'a pas quitté.
    const currentIndex = multiStepForm.currentStep.index;
    const stepErrors = steps.map((formStep, stepIndex) =>
        stepIndex === currentIndex ? undefined : formStep.validate?.(multiStepForm.formValues),
    );
    const blockingErrorMessage = stepErrors.find((error) => error !== undefined);
    const hasBlockingError = blockingErrorMessage !== undefined;

    // Valeur active de la barre d'onglets : étape courante, ou onglet additionnel
    // (indexé après les étapes du formulaire).
    const activeValue = activeExtraIndex !== null ? steps.length + activeExtraIndex : currentIndex;

    const handleSelectTab = (value: number) => {
        if (value < steps.length) {
            setActiveExtraIndex(null);
            multiStepForm.goToStep(value);
            return;
        }
        // Onglet additionnel : on persiste les valeurs de l'étape courante avant de basculer.
        multiStepForm.saveFormValues();
        setActiveExtraIndex(value - steps.length);
    };

    const isOnExtraTab = activeExtraIndex !== null;

    return (
        <Box
            width="100%"
            minHeight="60vh"
            display="flex"
            flexDirection="column"
        >
            <MultiStepFormHeader
                {...multiStepProps}
                stepErrors={stepErrors}
                extraTabs={extraTabs}
                activeValue={activeValue}
                onSelectTab={handleSelectTab}
            />
            {isOnExtraTab ? (
                extraTabs[activeExtraIndex as number].panel
            ) : (
                <>
                    <CurrentStep {...multiStepProps} />
                    <MultiStepFormFooter
                        {...multiStepProps}
                        hasBlockingError={hasBlockingError}
                        blockingErrorMessage={blockingErrorMessage}
                    />
                </>
            )}
        </Box>
    );
};

export default MultiStepForm;
